/**
 * SF-01 Specificity deficit (INV-CH-SF-01, ENGINE-SPEC §4; change C-SF01-DAMP).
 *
 * Anchoring density: numerals, mid-sentence proper nouns and direct quotations per 1,000 prose
 * words, `digits + 0.5·propers + 2·quotes`. Two-way, descending ramp (a low density reads as more
 * machine-like). The seed damped its confidence when the register ratio exceeded 0.2; here the
 * structural register never enters the prose exposure at all (C-SF01-DAMP), and κ comes from the
 * framework.
 */
import { REGISTER_PROSE } from '../document/types.ts';
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import { type CompiledEnglish, compileEnglishLexicons } from '../lexicons/index.ts';
import { derivePrior, makeEvidence, rateChannel } from './framework.ts';
import { fmt, proseParagraphs } from './helpers.ts';
import type { ChannelDef, Measurement, Ramp, RunContext } from './types.ts';

const RAMP: Ramp = [
	[2, 78],
	[8, 62],
	[15, 48],
	[30, 34],
	[60, 26],
];

const F_NUMERAL = 1;
const F_PROPER = 2;
const F_QUOTE = 3;

export const sf01Def: ChannelDef = {
	id: 'SF-01',
	name: 'Specificity deficit',
	family: 'TEXTURE',
	axis: 'authorship',
	registers: ['prose'],
	direction: -1,
	oneWay: false,
	estimator: 'rate',
	features: [
		{ id: 'specificity', label: 'Anchoring units per 1,000 words' },
		{ id: 'numeral', label: 'Numerals' },
		{ id: 'properNoun', label: 'Mid-sentence proper nouns (×0.5)' },
		{ id: 'quotation', label: 'Direct quotations (×2)' },
	],
	minExposure: 400,
	exposureUnit: 'words',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1000,
	unitLabel: 'per 1k words',
	prior: derivePrior(RAMP, 1000, 'words', 1.5),
	weight: 1.4,
	nd: 1,
	changes: ['C-SF01-DAMP'],
};

let lexCache: CompiledEnglish | undefined;
const lex = (): CompiledEnglish => {
	lexCache ??= compileEnglishLexicons(loadEnglishLexicons());
	return lexCache;
};

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const nr = lex().channels['ch-sf01'];
	const reNumeral = nr.get('numeral');
	const reProper = nr.get('properNoun');
	const reQuote = nr.get('quotation');
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	const ev = makeEvidence();
	let digits = 0;
	let propers = 0;
	let quotes = 0;
	for (const p of proseParagraphs(doc)) {
		if (doc.paragraphs.register[p] !== REGISTER_PROSE) continue;
		e[p] = doc.paragraphs.ownWords[p] as number;
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		const text = doc.text.slice(b.charStart, b.charEnd);
		const base = b.charStart;
		let kp = 0;
		reNumeral.lastIndex = 0;
		for (let m = reNumeral.exec(text); m; m = reNumeral.exec(text)) {
			ev.add(base + m.index, base + m.index + m[0].length, F_NUMERAL, 1);
			kp += 1;
			digits++;
		}
		reProper.lastIndex = 0;
		for (let m = reProper.exec(text); m; m = reProper.exec(text)) {
			// the proper noun itself is capture group 1; the lead-in punctuation is not evidence
			const noun = m[1] as string;
			const end = base + m.index + m[0].length;
			ev.add(end - noun.length, end, F_PROPER, 0.5);
			kp += 0.5;
			propers++;
		}
		reQuote.lastIndex = 0;
		for (let m = reQuote.exec(text); m; m = reQuote.exec(text)) {
			ev.add(base + m.index, base + m.index + m[0].length, F_QUOTE, 2);
			kp += 2;
			quotes++;
		}
		reNumeral.lastIndex = 0;
		reProper.lastIndex = 0;
		reQuote.lastIndex = 0;
		k[p] = kp;
	}
	const words = doc.proseWords;
	const density = words > 0 ? ((digits + 0.5 * propers + 2 * quotes) / words) * 1000 : 0;
	return {
		k,
		e,
		evidence: ev,
		features: { numerals: digits, propers, quotes },
		summary: [
			`${digits} numerals · ${propers} proper nouns · ${quotes} quotations → ${fmt(density, 1)}/1000w`,
		],
	};
}

export const sf01 = rateChannel(sf01Def, measure);
export { sf01Def as def, sf01 as impl };
